import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';

interface CFDTradingFormsProps {
  usdtBalance: number;
  availableBalance?: number;
  maxAllowedLeverage?: number;
  selectedPair: string;
  currentPrice: number;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onCFDTrade: (
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit'
  ) => void;
}

const CFDTradingForms: React.FC<CFDTradingFormsProps> = ({
  usdtBalance,
  availableBalance,
  maxAllowedLeverage = 100,
  selectedPair,
  currentPrice,
  calculateLiquidationPrice,
  onCFDTrade
}) => {
  const { t } = useTranslation();
  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('isolated');
  const [leverage, setLeverage] = useState(Math.min(10, maxAllowedLeverage));
  const [orderType, setOrderType] = useState<'limit' | 'market'>('market');
  const [longAmount, setLongAmount] = useState('');
  const [shortAmount, setShortAmount] = useState('');
  const [longPercentage, setLongPercentage] = useState(0);
  const [shortPercentage, setShortPercentage] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get Forex multiplier for lot calculation
  const getForexMultiplier = (symbol: string): number => {
    const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
    if (instrument && instrument.type === 'forex') {
      return 1000; // 1 lot = 1000 units of base currency for Forex
    }
    return 1; // 1 lot = 1 unit for other instruments
  };
  // Calculate amount from percentage for long positions
  const calculateLongAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const targetMargin = usdtBalance * (percentage / 100);
    const amount = (targetMargin * leverage) / (currentPrice * contractSize * forexMultiplier);
    
    // Ensure amount meets minimum trade size
    const minTradeSize = getMinTradeSize();
    const adjustedAmount = Math.max(amount, minTradeSize);
    
    return adjustedAmount.toFixed(getInstrumentType() === 'Stock' ? 0 : 5);
  }, [usdtBalance, leverage, currentPrice]);

  // Calculate amount from percentage for short positions
  const calculateShortAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const targetMargin = usdtBalance * (percentage / 100);
    const amount = (targetMargin * leverage) / (currentPrice * contractSize * forexMultiplier);
    
    // Ensure amount meets minimum trade size
    const minTradeSize = getMinTradeSize();
    const adjustedAmount = Math.max(amount, minTradeSize);
    
    return adjustedAmount.toFixed(getInstrumentType() === 'Stock' ? 0 : 5);
  }, [usdtBalance, leverage, currentPrice]);

  // Stop Loss / Take Profit states
  const [longStopLoss, setLongStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [longTakeProfit, setLongTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortStopLoss, setShortStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortTakeProfit, setShortTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });

  // Generate leverage options based on max allowed leverage
  const generateLeverageOptions = () => {
    const baseOptions = [1, 2, 3, 5, 10, 20, 25, 30, 50, 75, 100, 125, 150, 200, 250, 300, 400, 500, 750, 1000];
    return baseOptions.filter(option => option <= maxAllowedLeverage);
  };

  const leverageOptions = generateLeverageOptions();
  const percentageOptions = [25, 50, 75, 100];

  // Format required margin with dynamic decimal places
  const formatRequiredMargin = (value: number): string => {
    if (value === 0) return '0.00';
    if (value >= 1) return value.toFixed(2);
    if (value >= 0.01) return value.toFixed(4);
    if (value >= 0.0001) return value.toFixed(6);
    return value.toFixed(8);
  };

  // Clear error and success messages after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Real-time recalculation when leverage, price, or balance changes
  useEffect(() => {
    if (longPercentage > 0) {
      const newAmount = calculateLongAmountFromPercentage(longPercentage);
      setLongAmount(newAmount);
    }
  }, [leverage, currentPrice, usdtBalance, longPercentage, calculateLongAmountFromPercentage]);

  useEffect(() => {
    if (shortPercentage > 0) {
      const newAmount = calculateShortAmountFromPercentage(shortPercentage);
      setShortAmount(newAmount);
    }
  }, [leverage, currentPrice, usdtBalance, shortPercentage, calculateShortAmountFromPercentage]);

  // Get instrument type for display
  const getInstrumentType = () => {
    // Look up the instrument in CFD_INSTRUMENTS to get its type
    const instrument = CFD_INSTRUMENTS.find(item => item.symbol === selectedPair);
    if (instrument) {
      // Capitalize the first letter for display
      return instrument.type.charAt(0).toUpperCase() + instrument.type.slice(1);
    }
    
    // Default fallback
    return 'Unknown';
  };

  // Get unit label for display
  const getUnitLabel = () => {
    const instrumentType = getInstrumentType();
    return instrumentType === 'Forex' ? 'Lots' : instrumentType === 'Stock' ? 'Shares' : 'Units';
  };

  // Get contract size based on instrument type
  const getContractSize = () => {
    // For user-friendly trading, all contract sizes are 1
    // This means users trade in individual units (1 oz gold, 1 share, etc.)
    return 1;
  };

  // Get minimum trade size
  const getMinTradeSize = () => {
    // Unified minimum trade size across all CFD instruments
    return 0.01; // 0.01 lots for all instruments
  };

  // Debug current price for commodities
  useEffect(() => {
    if (selectedPair && getInstrumentType() === 'Commodity') {
      console.log(`CFDTradingForms - Commodity ${selectedPair} current price:`, currentPrice);
    }
  }, [selectedPair, currentPrice]);

  // Get spread (simulated)
  const getSpread = () => {
    const type = getInstrumentType();
    switch (type) {
      case 'Forex': return currentPrice * 0.00002; // 2 pips
      case 'Commodity': return currentPrice * 0.0005; // 0.05%
      case 'Stock': return currentPrice * 0.001; // 0.1%
      default: return currentPrice * 0.0001;
    }
  };

  // Calculate estimated PnL for stop loss and take profit
  const calculateEstimatedPnl = (
    side: 'long' | 'short',
    entryPrice: number,
    triggerPrice: number,
    amount: number,
    leverage: number
  ): number => {
    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    
    if (side === 'long') {
      // For long positions: (triggerPrice - entryPrice) * amount * contractSize
      return (triggerPrice - entryPrice) * amount * contractSize * forexMultiplier;
    } else {
      // For short positions: (entryPrice - triggerPrice) * amount * contractSize
      return (entryPrice - triggerPrice) * amount * contractSize * forexMultiplier;
    }
  };

  const handleLong = () => {
    const amount = parseFloat(longAmount);
    if (!amount || amount < getMinTradeSize()) {
      setErrorMessage(`Minimum trade size is ${getMinTradeSize()}`);
      return;
    }

    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const notionalValue = amount * currentPrice * contractSize * forexMultiplier;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    const stopLoss = longStopLoss.enabled && longStopLoss.price ? {
      trigger_price: parseFloat(longStopLoss.price),
      execution_type: longStopLoss.type,
      execution_price: longStopLoss.type === 'limit' && longStopLoss.limitPrice ? parseFloat(longStopLoss.limitPrice) : undefined,
    } : undefined;

    const takeProfit = longTakeProfit.enabled && longTakeProfit.price ? {
      trigger_price: parseFloat(longTakeProfit.price),
      execution_type: longTakeProfit.type,
      execution_price: longTakeProfit.type === 'limit' && longTakeProfit.limitPrice ? parseFloat(longTakeProfit.limitPrice) : undefined,
    } : undefined;

    onCFDTrade('long', amount * forexMultiplier, leverage, marginType, stopLoss, takeProfit, orderType, contractSize);
    setSuccessMessage(`Long position opened successfully for ${amount} ${getInstrumentType() === 'Forex' ? 'lots' : getInstrumentType() === 'Stock' ? 'shares' : 'units'}`);
    setLongAmount('');
    setLongPercentage(0);
    setLongStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
    setLongTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
  };

  const handleShort = () => {
    const amount = parseFloat(shortAmount);
    if (!amount || amount < getMinTradeSize()) {
      setErrorMessage(`Minimum trade size is ${getMinTradeSize()}`);
      return;
    }

    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const notionalValue = amount * currentPrice * contractSize * forexMultiplier;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    const stopLoss = shortStopLoss.enabled && shortStopLoss.price ? {
      trigger_price: parseFloat(shortStopLoss.price),
      execution_type: shortStopLoss.type,
      execution_price: shortStopLoss.type === 'limit' && shortStopLoss.limitPrice ? parseFloat(shortStopLoss.limitPrice) : undefined,
    } : undefined;

    const takeProfit = shortTakeProfit.enabled && shortTakeProfit.price ? {
      trigger_price: parseFloat(shortTakeProfit.price),
      execution_type: shortTakeProfit.type,
      execution_price: shortTakeProfit.type === 'limit' && shortTakeProfit.limitPrice ? parseFloat(shortTakeProfit.limitPrice) : undefined,
    } : undefined;

    onCFDTrade('short', amount * forexMultiplier, leverage, marginType, stopLoss, takeProfit, orderType, contractSize);
    setSuccessMessage(`Short position opened successfully for ${amount} ${getInstrumentType() === 'Forex' ? 'lots' : getInstrumentType() === 'Stock' ? 'shares' : 'units'}`);
    setShortAmount('');
    setShortPercentage(0);
    setShortStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
    setShortTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
  };

  const handleLongPercentage = (percentage: number) => {
    setLongPercentage(percentage);
    const newAmount = calculateLongAmountFromPercentage(percentage);
    setLongAmount(newAmount);
  };

  const handleShortPercentage = (percentage: number) => {
    setShortPercentage(percentage);
    const newAmount = calculateShortAmountFromPercentage(percentage);
    setShortAmount(newAmount);
  };

  const calculateLongCost = () => {
    const amount = parseFloat(longAmount) || 0;
    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const notionalValue = amount * currentPrice * contractSize * forexMultiplier;
    return notionalValue / leverage;
  };

  const calculateShortCost = () => {
    const amount = parseFloat(shortAmount) || 0;
    const contractSize = getContractSize();
    const forexMultiplier = getForexMultiplier(selectedPair);
    const notionalValue = amount * currentPrice * contractSize * forexMultiplier;
    return notionalValue / leverage;
  };

  // Calculate estimated PnL for long stop loss
  const longStopLossPnl = longStopLoss.enabled && longStopLoss.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        currentPrice,
        parseFloat(longStopLoss.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for long take profit
  const longTakeProfitPnl = longTakeProfit.enabled && longTakeProfit.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        currentPrice,
        parseFloat(longTakeProfit.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short stop loss
  const shortStopLossPnl = shortStopLoss.enabled && shortStopLoss.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        currentPrice,
        parseFloat(shortStopLoss.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short take profit
  const shortTakeProfitPnl = shortTakeProfit.enabled && shortTakeProfit.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        currentPrice,
        parseFloat(shortTakeProfit.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  const instrumentType = getInstrumentType();
  const contractSize = getContractSize();
  const spread = getSpread();
  const minTradeSize = getMinTradeSize();

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-slate-700/50 shadow-2xl">
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{errorMessage}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 md:mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{successMessage}</span>
        </div>
      )}

      {/* Instrument Info */}
      <div className="mb-4 md:mb-6 bg-slate-900/50 rounded-xl p-3 md:p-4 border border-slate-700/50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 text-sm">
          <div>
            <span className="text-slate-400">Type:</span>
            <div className="font-medium text-white">{instrumentType}</div>
          </div>
          <div>
            <span className="text-slate-400">Contract Size:</span>
            <div className="font-medium text-white">{contractSize.toLocaleString()}</div>
          </div>
          <div>
            <span className="text-slate-400">Spread:</span>
            <div className="font-medium text-white">{spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</div>
          </div>
          <div>
            <span className="text-slate-400">Min Size:</span>
            <div className="font-medium text-white">{minTradeSize}</div>
          </div>
        </div>
      </div>

      {/* Margin Type and Leverage Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-8 gap-3 md:gap-0">
        <div className="flex items-center gap-3 md:gap-6">
          <div className="flex bg-slate-900/50 rounded-xl p-1 border border-slate-600/30">
            <button
              onClick={() => setMarginType('isolated')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'isolated'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              {t('trading.isolated')}
            </button>
            <button
              onClick={() => setMarginType('cross')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'cross'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              {t('trading.cross')}
            </button>
          </div>
          
          <div className="text-orange-400 font-bold text-2xl bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
            {leverage}x
          </div>
        </div>

        {/* Leverage Selector */}
        <div className="flex items-center gap-2 md:gap-4">
          <span className="text-slate-400 text-xs md:text-sm">{t('trading.leverage')}:</span>
          <div className="flex gap-2">
            {leverageOptions.map((lev) => (
              <button
                key={lev}
                onClick={() => setLeverage(Math.min(lev, maxAllowedLeverage))}
                className={`px-3 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                  leverage === lev
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 transform scale-105'
                    : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-600/30'
                }`}
              >
                {lev}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex gap-3 md:gap-6 mb-4 md:mb-8">
        <button
          onClick={() => setOrderType('market')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'market' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          {t('trading.market')}
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'limit' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          {t('trading.limit')}
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-8">
        {/* Long Form */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono">
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USD
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1 md:mb-3">{t('common.price')} (USD)</label>
              <input
                type="text"
                value={currentPrice.toFixed(instrumentType === 'Forex' ? 5 : 2)}
                className="w-full bg-transparent text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1 md:mb-3">
                {t('trading.size')} ({getUnitLabel()})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Min ${minTradeSize}`}
                  value={longAmount}
                  onChange={(e) => {
                    setLongAmount(e.target.value);
                    setLongPercentage(0);
                  }}
                  className="w-full bg-transparent text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium" translate="no">
                  {getUnitLabel().toUpperCase()}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleLongPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    longPercentage === percentage 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-3 md:pt-6 mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longStopLoss.enabled}
                  onChange={(e) => setLongStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">{t('trading.stopLoss')}</label>
              </div>
              {longStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('common.price')}
                      value={longStopLoss.price}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {longStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${longStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={longStopLoss.type}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('trading.market')}</option>
                      <option value="limit">{t('trading.limit')}</option>
                    </select>
                    {longStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('common.price')}
                        value={longStopLoss.limitPrice}
                        onChange={(e) => setLongStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div className="mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longTakeProfit.enabled}
                  onChange={(e) => setLongTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">{t('trading.takeProfit')}</label>
              </div>
              {longTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('common.price')}
                      value={longTakeProfit.price}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {longTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${longTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={longTakeProfit.type}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('trading.market')}</option>
                      <option value="limit">{t('trading.limit')}</option>
                    </select>
                    {longTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('common.price')}
                        value={longTakeProfit.limitPrice}
                        onChange={(e) => setLongTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>{t('trading.requiredMargin')}</span>
                <span>{t('trading.spread')}: <span translate="no">{spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</span></span>
              </div>
              <div className="text-slate-300 font-mono text-lg" translate="no">{formatRequiredMargin(calculateLongCost())} USD</div>
            </div>

            <button 
              onClick={handleLong}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/25 transform hover:scale-105"
            >
              {t('trading.buy')}/{t('trading.long')}
            </button>
          </div>
        </div>

        {/* Short Form */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono">
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USD
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1 md:mb-3">{t('common.price')} (USD)</label>
              <input
                type="text"
                value={currentPrice.toFixed(instrumentType === 'Forex' ? 5 : 2)}
                className="w-full bg-transparent text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1 md:mb-3">
                {t('trading.size')} ({getUnitLabel()})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Min ${minTradeSize}`}
                  value={shortAmount}
                  onChange={(e) => {
                    setShortAmount(e.target.value);
                    setShortPercentage(0);
                  }}
                  className="w-full bg-transparent text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium" translate="no">
                  {getUnitLabel().toUpperCase()}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleShortPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    shortPercentage === percentage 
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-3 md:pt-6 mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortStopLoss.enabled}
                  onChange={(e) => setShortStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">{t('trading.stopLoss')}</label>
              </div>
              {shortStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('common.price')}
                      value={shortStopLoss.price}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {shortStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${shortStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={shortStopLoss.type}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('trading.market')}</option>
                      <option value="limit">{t('trading.limit')}</option>
                    </select>
                    {shortStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('common.price')}
                        value={shortStopLoss.limitPrice}
                        onChange={(e) => setShortStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div className="mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortTakeProfit.enabled}
                  onChange={(e) => setShortTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">{t('trading.takeProfit')}</label>
              </div>
              {shortTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('common.price')}
                      value={shortTakeProfit.price}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {shortTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${shortTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={shortTakeProfit.type}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('trading.market')}</option>
                      <option value="limit">{t('trading.limit')}</option>
                    </select>
                    {shortTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('common.price')}
                        value={shortTakeProfit.limitPrice}
                        onChange={(e) => setShortTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>{t('trading.requiredMargin')}</span>
                <span>{t('trading.spread')}: <span translate="no">{spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</span></span>
              </div>
              <div className="text-slate-300 font-mono text-lg" translate="no">{formatRequiredMargin(calculateShortCost())} USD</div>
            </div>

            <button 
              onClick={handleShort}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 transform hover:scale-105"
            >
              {t('trading.sell')}/{t('trading.short')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CFDTradingForms;